import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../../components/layout/Navbar';
import Footer from '../../../components/layout/Footer';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useLearnAuth } from '@/hooks/useLearnAuth';
import { calculateOverallProgress } from '@/utils/progressTracking';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { toast } from 'sonner';
import { ArrowLeft, Calculator } from 'lucide-react';

const CapstoneROI: React.FC = () => {
  const { user } = useLearnAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingCapstone, setExistingCapstone] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    processName: '',
    hoursPerWeek: '',
    hourlyRate: '',
    automationPercent: '',
    toolCostMonthly: '',
    implementationHours: '',
    notes: ''
  });

  const [roi, setRoi] = useState({
    timeSavedPerWeek: 0,
    timeSavedPerYear: 0,
    costSavingsPerYear: 0,
    toolCostPerYear: 0,
    implementationCost: 0,
    netSavingsYear1: 0,
    netSavingsYear2: 0,
    roiPercentage: 0,
    breakEvenMonths: 0
  });

  useEffect(() => {
    if (!user) return;

    const checkEligibility = async () => {
      try {
        // Check if all 16 modules completed
        const progress = await calculateOverallProgress(user.id);
        if (progress.completedModules < 16) {
          toast.error('Complete all 16 modules before starting capstones');
          navigate('/learn/app/modules');
          return;
        }

        // Check for existing capstone
        const { data: capstoneData } = await supabase
          .from('capstones')
          .select('*')
          .eq('user_id', user.id)
          .eq('type', 'roi')
          .maybeSingle();

        if (capstoneData) {
          setExistingCapstone(capstoneData);
          const roiData = capstoneData.roi_calc as any;
          if (roiData) {
            setFormData({
              processName: roiData.processName || '',
              hoursPerWeek: roiData.hoursPerWeek || '',
              hourlyRate: roiData.hourlyRate || '',
              automationPercent: roiData.automationPercent || '',
              toolCostMonthly: roiData.toolCostMonthly || '',
              implementationHours: roiData.implementationHours || '',
              notes: roiData.notes || ''
            });
          }
        }
      } catch (error) {
        console.error('Error checking eligibility:', error);
      } finally {
        setLoading(false);
      }
    };

    checkEligibility();
  }, [user, navigate]);

  useEffect(() => {
    calculateROI();
  }, [formData]);

  const calculateROI = () => {
    const hours = parseFloat(formData.hoursPerWeek) || 0;
    const rate = parseFloat(formData.hourlyRate) || 0;
    const autoPercent = parseFloat(formData.automationPercent) || 0;
    const toolCost = parseFloat(formData.toolCostMonthly) || 0;
    const implHours = parseFloat(formData.implementationHours) || 0;

    const timeSavedPerWeek = hours * (autoPercent / 100);
    const timeSavedPerYear = timeSavedPerWeek * 52;
    const costSavingsPerYear = timeSavedPerYear * rate;
    const toolCostPerYear = toolCost * 12;
    const implementationCost = implHours * rate;
    const netSavingsYear1 = costSavingsPerYear - toolCostPerYear - implementationCost;
    const netSavingsYear2 = costSavingsPerYear - toolCostPerYear;
    const totalCost = toolCostPerYear + implementationCost;
    const roiPercentage = totalCost > 0 ? ((costSavingsPerYear - totalCost) / totalCost) * 100 : 0;
    const breakEvenMonths = costSavingsPerYear > toolCost ? (implementationCost / ((costSavingsPerYear - toolCostPerYear) / 12)) : 0;

    setRoi({
      timeSavedPerWeek,
      timeSavedPerYear,
      costSavingsPerYear,
      toolCostPerYear,
      implementationCost,
      netSavingsYear1,
      netSavingsYear2,
      roiPercentage,
      breakEvenMonths
    });
  };

  const handleSubmit = async (status: 'draft' | 'submitted') => {
    if (!user) return;

    // Validation for submission
    if (status === 'submitted') {
      if (!formData.processName || !formData.hoursPerWeek || !formData.hourlyRate || 
          !formData.automationPercent || !formData.toolCostMonthly) {
        toast.error('Please fill in all required fields');
        return;
      }
    }

    setSubmitting(true);

    try {
      const capstoneData = {
        user_id: user.id,
        type: 'roi',
        status,
        roi_calc: {
          ...formData,
          calculatedRoi: roi
        }
      };

      if (existingCapstone) {
        const { error } = await supabase
          .from('capstones')
          .update(capstoneData)
          .eq('id', existingCapstone.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('capstones')
          .insert(capstoneData);

        if (error) throw error;
      }

      toast.success(status === 'submitted' ? 'ROI Calculator submitted successfully!' : 'Progress saved');
      if (status === 'submitted') {
        navigate('/learn/app/dashboard');
      }
    } catch (error) {
      console.error('Error saving capstone:', error);
      toast.error('Failed to save capstone');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const isSubmitted = existingCapstone?.status === 'submitted';

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-4xl">
          <Button variant="ghost" onClick={() => navigate('/learn/app/dashboard')} className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <Card className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <Calculator className="w-8 h-8 text-brand-navy" />
              <div>
                <h1 className="text-3xl font-bold text-brand-navy">ROI Calculator Capstone</h1>
                <p className="text-gray-600">Calculate the return on investment for your AI implementation</p>
              </div>
            </div>

            {isSubmitted && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 font-medium">✅ Capstone submitted! Your work is under review.</p>
              </div>
            )}

            <div className="space-y-6">
              <div>
                <Label htmlFor="processName">Process/Task Name *</Label>
                <Input
                  id="processName"
                  value={formData.processName}
                  onChange={(e) => setFormData({ ...formData, processName: e.target.value })}
                  placeholder="e.g., Customer invoice processing"
                  disabled={isSubmitted}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="hoursPerWeek">Hours per Week *</Label>
                  <Input
                    id="hoursPerWeek"
                    type="number"
                    step="0.5"
                    value={formData.hoursPerWeek}
                    onChange={(e) => setFormData({ ...formData, hoursPerWeek: e.target.value })}
                    placeholder="e.g., 10"
                    disabled={isSubmitted}
                  />
                </div>

                <div>
                  <Label htmlFor="hourlyRate">Hourly Rate (NZD) *</Label>
                  <Input
                    id="hourlyRate"
                    type="number"
                    step="1"
                    value={formData.hourlyRate}
                    onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })}
                    placeholder="e.g., 50"
                    disabled={isSubmitted}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="automationPercent">Automation % *</Label>
                  <Input
                    id="automationPercent"
                    type="number"
                    step="5"
                    min="0"
                    max="100"
                    value={formData.automationPercent}
                    onChange={(e) => setFormData({ ...formData, automationPercent: e.target.value })}
                    placeholder="e.g., 70"
                    disabled={isSubmitted}
                  />
                  <p className="text-sm text-gray-500 mt-1">What % of the task can AI automate?</p>
                </div>

                <div>
                  <Label htmlFor="toolCostMonthly">Tool Cost per Month (NZD) *</Label>
                  <Input
                    id="toolCostMonthly"
                    type="number"
                    step="1"
                    value={formData.toolCostMonthly}
                    onChange={(e) => setFormData({ ...formData, toolCostMonthly: e.target.value })}
                    placeholder="e.g., 50"
                    disabled={isSubmitted}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="implementationHours">Implementation Hours</Label>
                <Input
                  id="implementationHours"
                  type="number"
                  step="1"
                  value={formData.implementationHours}
                  onChange={(e) => setFormData({ ...formData, implementationHours: e.target.value })}
                  placeholder="e.g., 20"
                  disabled={isSubmitted}
                />
                <p className="text-sm text-gray-500 mt-1">Hours to set up and integrate the AI solution</p>
              </div>

              <div>
                <Label htmlFor="notes">Notes & Assumptions</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Describe your implementation plan, assumptions, and considerations..."
                  rows={4}
                  disabled={isSubmitted}
                />
              </div>

              {/* ROI Results */}
              <Card className="p-6 bg-blue-50 border-blue-200">
                <h3 className="font-semibold text-xl mb-4 text-brand-navy">Calculated ROI</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Time Saved per Week</p>
                    <p className="text-2xl font-bold text-brand-navy">{roi.timeSavedPerWeek.toFixed(1)} hours</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Time Saved per Year</p>
                    <p className="text-2xl font-bold text-brand-navy">{roi.timeSavedPerYear.toFixed(0)} hours</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Annual Cost Savings</p>
                    <p className="text-2xl font-bold text-green-600">${roi.costSavingsPerYear.toFixed(0)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Annual Tool Cost</p>
                    <p className="text-2xl font-bold text-red-600">${roi.toolCostPerYear.toFixed(0)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Implementation Cost</p>
                    <p className="text-2xl font-bold text-gray-700">${roi.implementationCost.toFixed(0)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Net Savings Year 1</p>
                    <p className={`text-2xl font-bold ${roi.netSavingsYear1 >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${roi.netSavingsYear1.toFixed(0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">ROI Percentage</p>
                    <p className={`text-2xl font-bold ${roi.roiPercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {roi.roiPercentage.toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Break-even Point</p>
                    <p className="text-2xl font-bold text-brand-navy">
                      {roi.breakEvenMonths > 0 ? `${roi.breakEvenMonths.toFixed(1)} months` : 'N/A'}
                    </p>
                  </div>
                </div>
              </Card>

              {/* Actions */}
              {!isSubmitted && (
                <div className="flex gap-4">
                  <Button
                    variant="outline"
                    onClick={() => handleSubmit('draft')}
                    disabled={submitting}
                    className="flex-1"
                  >
                    Save Draft
                  </Button>
                  <Button
                    onClick={() => handleSubmit('submitted')}
                    disabled={submitting}
                    className="flex-1"
                  >
                    Submit for Review
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default CapstoneROI;